import { useEffect, useState } from 'react';
import { createSignedAssetUrl } from '../../lib/storage';
import type { CommunityTemplateGalleryRead } from '../../types/api';
import { communityBackdropUrl } from '../templates/communityTemplatePrefill';
import { fetchCommunityTemplate, listCommunityTemplates } from '../templates/communityTemplatesApi';
import type { CommunityPreview } from './SlideCanvas';

/**
 * The design and zone map behind a community-template run, for the editor's
 * stage. The request row carries only the template id, so the map is looked
 * up in two places, in order:
 *
 * 1. The gallery — global, public previews — which holds every approved
 *    template, the business's own and everybody else's.
 * 2. The business's own templates, for the one the gallery does not list: a
 *    business may post on its own template in any status (a draft, one with
 *    the reviewers, one sent back), and without this the editor would fall
 *    back to id-labelled fields with no defaults or limits. Its image is a
 *    private upload, so it is signed like any other asset.
 *
 * The gallery is loaded once and shared across mounts through a module-level
 * cache: a review session opens the editor repeatedly and the gallery does not
 * move under it. Every failure — the template gone from both places, a list
 * unreachable — resolves to null, which the stage reads as "draw the classic
 * block": a degraded preview, never an error, exactly like a missing photo.
 */

let galleryPromise: Promise<CommunityTemplateGalleryRead[]> | null = null;

function loadGallery(): Promise<CommunityTemplateGalleryRead[]> {
  galleryPromise ??= listCommunityTemplates().catch((caught: unknown) => {
    // A failed load is not cached: the next mount may be after the network came back.
    galleryPromise = null;
    throw caught;
  });
  return galleryPromise;
}

/** The template out of the gallery, or null when it is not there or it did not load. */
async function fromGallery(templateId: string): Promise<CommunityPreview | null> {
  try {
    const templates = await loadGallery();
    const found = templates.find((entry) => entry.id === templateId) ?? null;
    // The design's own image: `image_url`, or a v1 template's upload in
    // `preview_url`. Null for a colour or gradient design.
    return found === null ? null : { zones: found.zones, imageUrl: communityBackdropUrl(found) };
  } catch {
    return null;
  }
}

/** The business's own template, whatever its status, or null when it is not theirs. */
async function fromOwn(
  businessId: string,
  templateId: string,
  signal: AbortSignal,
): Promise<CommunityPreview | null> {
  try {
    const template = await fetchCommunityTemplate(businessId, templateId, signal);
    let imageUrl: string | null = null;
    if (template.image_path) {
      try {
        imageUrl = await createSignedAssetUrl(template.image_path);
      } catch {
        // The map still labels the fields; the stage just has no backdrop.
        imageUrl = null;
      }
    }
    return { zones: template.zones, imageUrl };
  } catch {
    return null;
  }
}

interface Resolved {
  key: string;
  preview: CommunityPreview | null;
}

export function useCommunityPreview(
  businessId: string,
  templateId: string | null,
): CommunityPreview | null {
  // The answer is stored with the ids it answers, and derived against the
  // current ones on render — a stale answer for a previous id reads as null
  // rather than being cleared by a second setState.
  const [resolved, setResolved] = useState<Resolved | null>(null);
  const key = templateId === null ? null : `${businessId}/${templateId}`;

  useEffect(() => {
    if (templateId === null) return;
    const controller = new AbortController();
    const answering = `${businessId}/${templateId}`;

    void (async () => {
      const preview =
        (await fromGallery(templateId)) ??
        (await fromOwn(businessId, templateId, controller.signal));
      if (!controller.signal.aborted) setResolved({ key: answering, preview });
    })();

    return () => controller.abort();
  }, [businessId, templateId]);

  return key !== null && resolved?.key === key ? resolved.preview : null;
}
